import { useEffect, useState } from 'react';
import { Route, Redirect } from 'react-router-dom';
import { useAuth } from '@repo/shared';

/**
 * ProtectedRoute component
 * 
 * Wraps routes to enforce authentication and/or specific roles
 * 
 * Usage (as wrapper component):
 * <ProtectedRoute 
 *   component={ClassesPage} 
 *   requiredRoles={['admin', 'teacher']}
 * />
 * 
 * Usage (as Route):
 * <ProtectedRoute 
 *   exact
 *   path="/admin"
 *   component={AdminPage} 
 *   requiredRoles={['admin']}
 * />
 */
export default function ProtectedRoute({ 
  component: Component, 
  requiredRoles = null,
  roleRequired = null, // Backward compatibility
  path = null,
  onUnauthorized,
  render,
  ...rest 
}) {
  const { user, isAuthenticated } = useAuth();
  const [isAuthorized, setIsAuthorized] = useState(false);
  
  // Support both requiredRoles (array) and roleRequired (string) for backward compatibility
  const roles = requiredRoles || (roleRequired ? [roleRequired] : null);

  useEffect(() => {
    // Check if user is authenticated
    if (!isAuthenticated) {
      setIsAuthorized(false);
      if (onUnauthorized) {
        onUnauthorized('NOT_AUTHENTICATED');
      }
      return;
    }

    // Check role if required
    if (roles && roles.length > 0) {
      if (!roles.includes(user?.role)) {
        console.warn(
          `Access denied: User role "${user?.role}" not in allowed roles [${roles.join(', ')}]`,
          path ? `Attempted to access: ${path}` : ''
        );
        setIsAuthorized(false);
        if (onUnauthorized) {
          onUnauthorized('INSUFFICIENT_ROLE');
        }
        return;
      }
    }

    // User is authorized
    setIsAuthorized(true);
  }, [isAuthenticated, user, roles, onUnauthorized, path]);

  // Unauthorized access message component
  const UnauthorizedMessage = () => (
    <div className="alert alert-danger m-4" role="alert">
      <h4 className="alert-heading">Access Denied</h4>
      <p>
        Your user role <strong>"{user?.role}"</strong> does not have access to this page.
      </p>
      <hr />
      <p className="mb-0">
        Contact an administrator if you believe this is an error.
      </p>
    </div>
  );

  // When used as a Route (path prop provided)
  if (path) {
    return (
      <Route
        path={path}
        {...rest}
        render={(props) => {
          if (!isAuthenticated) {
            return <Redirect to="/" />;
          }
          if (!isAuthorized) {
            return <UnauthorizedMessage />;
          }
          
          // Extract route params and pass them as individual props
          const routeParams = props.match.params;
          return render 
            ? render(props) 
            : <Component {...props} user={user} {...routeParams} />;
        }}
      />
    );
  }

  // When used as a wrapper component
  if (!isAuthorized) {
    return <UnauthorizedMessage />;
  }

  return <Component user={user} {...rest} />;
}
